import React from 'react'
import { FormGroup, FormControlLabel, useTheme } from '@mui/material'
import { SecurityUser } from 'iconsax-react'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import Switch from '@SonarCX/components/atoms/Switch'
import RolesMenu from '@SonarCX/components/molecules/RolesMenu'
import Button from '@SonarCX/components/atoms/Button2'

import { PERMS } from '@SonarCX/hooks/useAccess'
import { AddSuccessAlert } from '@SonarCX/slices/alert'
import { selectRole } from '@SonarCX/slices/role'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useAreYouSure } from '@SonarCX/providers/AreYouSureProvider'
import useStyles from './styles'

type AccessViewProps = {
  user: LocalUser
  canEdit: boolean
}

const AccessView: React.FC<AccessViewProps> = ({ user, canEdit }) => {
  const theme = useTheme()

  const dispatch = useAppDispatch()

  const { roles } = useAppSelector(selectRole)

  const { uploadUser } = useUserAPI()
  const { setTitle, setReason, setSubtext, setV2, setAction } = useAreYouSure()

  const [active, setActive] = React.useState(user.auth.suspended_at === null)

  const selectableRoles = React.useMemo(
    () => [
      null,
      ...Object.values(roles)
        .filter(role => role.permissions.includes(PERMS.AppPortal))
        .map(role => role.id)
    ],
    [roles]
  )

  const classes = useStyles()

  const toggleChecked = React.useCallback(
    (checked: boolean) => {
      const action = () => {
        setActive(checked)

        const suspended_at = checked ? null : new Date().toISOString()
        const updated = {
          ...user,
          auth: {
            ...user.auth,
            suspended_at
          }
        }

        uploadUser(updated, false, () => {
          dispatch(AddSuccessAlert('Changes saved'))
        })
      }

      if (checked) {
        action()
        return
      }

      setTitle('ARE YOU SURRE?')
      setReason("Are you sure you want to disable this user's access?")
      setSubtext('This will prevent them from logging into the portal.')
      setV2(true)
      setAction(action)
    },
    [
      uploadUser,
      user,
      dispatch,
      setTitle,
      setReason,
      setSubtext,
      setV2,
      setAction
    ]
  )

  const updateRole = React.useCallback(
    (id: string) => {
      const updated = {
        ...user,
        role_id: id
      }

      uploadUser(updated, false, () => {
        dispatch(AddSuccessAlert('Changes saved'))
      })
    },
    [user, uploadUser, dispatch]
  )

  const handleRevoke = React.useCallback(() => {
    const action = () => {
      const updated = {
        ...user,
        // Empty string will become null in uploadUser
        auth: 'null'
      }

      uploadUser(updated, false, () => {
        dispatch(AddSuccessAlert('Changes saved'))
      })
    }

    setTitle('ARE YOU SURRE?')
    setReason("Are you sure you want to revoke this user's access?")
    setSubtext(
      'This will remove their access to the portal and they will no longer be able to log in.'
    )
    setV2(true)
    setAction(action)
  }, [
    uploadUser,
    user,
    dispatch,
    setTitle,
    setReason,
    setSubtext,
    setV2,
    setAction
  ])

  return (
    <>
      <div className={classes.row}>
        <div>Email</div>

        <div className={classes.email}>{user.auth.email || 'No email set'}</div>
      </div>

      <div className={classes.row}>
        <div>Active</div>

        <FormGroup>
          <FormControlLabel
            label=''
            control={
              <Switch
                disabled={!canEdit}
                checked={active}
                onChange={e => toggleChecked(e.target.checked)}
                className={classes.switch}
              />
            }
          />
        </FormGroup>
      </div>

      <div className={classes.row}>
        <div className={classes.role}>
          <SecurityUser variant='Bold' color='grey' size={theme.size.base} />
          Role
        </div>

        {selectableRoles.length === 0 ? (
          <div>No portal roles available</div>
        ) : (
          <RolesMenu
            selected={user.role_id}
            customOptions={selectableRoles}
            onSelect={id => updateRole(id)}
          />
        )}
      </div>

      {canEdit && (
        <Button
          disabled={!canEdit}
          onClick={handleRevoke}
          className={classes.button}
        >
          Revoke Access
        </Button>
      )}
    </>
  )
}

export default AccessView
